import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, MapPin, Clock, Instagram, MessageCircle, Mail, ShieldCheck, ExternalLink, Link2 } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { QuoteForm } from "@/components/QuoteForm";
import { QuickContact } from "@/components/QuickContact";
import { seoHead, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";

export const Route = createFileRoute("/contact")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) => seoHead({
    path: "/contact",
    title: "Contact Woodseer Autos | MOT & Car Repairs East London E14",
    description: `Call ${NAP.phone} — fastest reply. WhatsApp or request a callback. 14 Copenhagen Place (yard entrance), Poplar E14 7DF. Mon–Sat 9am–6pm.`,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Contact", path: "/contact" },
    ],
    jsonLd: [localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance))],
  }),
  component: ContactPage,
});

function ContactPage() {
  const biz = getBusinessConfig();

  return (
    <>
      <section className="section-y">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <p className="eyebrow">Get in touch</p>
            <h1 className="mt-4 display-xl text-foreground">Talk to a real mechanic.</h1>
            <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Phone, WhatsApp, email or pop in. The fastest way to reach us is on {biz.phone} during workshop hours.
            </p>
          </div>

          {/* Direct contact hub — first thing visitors see */}
          <div className="mt-10">
            <QuickContact location="contact_top" eyebrow="Reach us in seconds" headline="Every way to contact Woodseer Autos." subline="Call or WhatsApp for the fastest reply. Need directions, our Instagram or all our links? Tap below or scan the code." />
          </div>

          {/* Official contact channels — verification record */}
          <div className="mt-10 rounded-2xl border border-primary/25 bg-primary/5 p-6 md:p-8">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary border border-primary/25 shrink-0">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">Official contact channels</p>
                <h2 className="mt-1 text-xl md:text-2xl font-bold text-foreground tracking-tight">
                  Verified ways to reach Woodseer Autos.
                </h2>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  These are the only official channels for {biz.name}. Domain emails end in <code className="text-foreground font-semibold">@woodseerautos.co.uk</code>.
                </p>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <ChannelLink href={biz.phoneHref} icon={Phone} label={biz.phone} sub="Workshop line · fastest" />
                  <ChannelLink href={`mailto:${biz.email}`} icon={Mail} label={biz.email} sub="Customer enquiries (domain email)" />
                  <ChannelLink href="mailto:woodseerautos@gmail.com" icon={Mail} label="woodseerautos@gmail.com" sub="Backup / admin verification" />
                  <ChannelLink href={biz.whatsappUrl} icon={MessageCircle} label="WhatsApp" sub="Same number, message us anytime" external />
                  <ChannelLink href={biz.instagramUrl} icon={Instagram} label="@woodseerautos" sub="Official Instagram" external />
                  <ChannelLink href={biz.linktreeUrl} icon={Link2} label="linktr.ee/woodseerautos" sub="All Woodseer Autos links" external />
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <Link to="/official-business-information" className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-xs font-bold text-primary-foreground hover:bg-primary/90">
                    Full official business record <ExternalLink className="h-3 w-3" />
                  </Link>
                  <span className="text-[11px] text-muted-foreground">
                    DVSA MOT Station VTS <strong className="text-foreground">{biz.vtsNumber}</strong> · Classes 1, 2, 3, 4, 5 & 7
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      <section className="pb-16 md:pb-20">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Contact Info */}
            <div className="space-y-4">
              <div>
                <p className="eyebrow">Direct lines</p>
                <h2 className="mt-2 text-2xl md:text-3xl font-bold text-foreground tracking-tight">Contact details</h2>
                <p className="mt-2 text-sm text-muted-foreground">The fastest way to reach us is by phone.</p>
              </div>

              <a href={biz.phoneHref} className="flex items-center gap-4 rounded-2xl surface-premium surface-premium-hover p-5">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/12 to-primary/4 border border-primary/15">
                  <Phone className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Phone</p>
                  <p className="text-lg font-bold text-foreground tracking-tight">{biz.phone}</p>
                </div>
              </a>

              <a href={`mailto:${biz.email}`} className="flex items-center gap-4 rounded-2xl surface-premium surface-premium-hover p-5">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/12 to-primary/4 border border-primary/15">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Email</p>
                  <p className="text-base font-bold text-foreground">{biz.email}</p>
                </div>
              </a>

              <div className="flex items-start gap-4 rounded-2xl surface-premium p-5">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/12 to-primary/4 border border-primary/15 shrink-0">
                  <MapPin className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Yard entrance · sat-nav</p>
                  <p className="mt-1 font-bold text-foreground">14 Copenhagen Place, London E14 7DF</p>
                  <div className="mt-3 pt-3 border-t border-border/60">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Registered</p>
                    <p className="mt-1 text-sm text-foreground/80">{biz.address}, {biz.addressLine2}, {biz.postcode}</p>
                  </div>
                  {biz.entrance && (
                    <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
                      <span className="font-semibold text-foreground/80">How to find us:</span> {biz.entrance}
                    </p>
                  )}
                  {biz.vtsNumber && (
                    <p className="mt-2 text-[11px] font-semibold text-muted-foreground">DVSA VTS: {biz.vtsNumber}</p>
                  )}
                  <a href={biz.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:text-primary/80 mt-3 inline-block font-bold">
                    Get directions →
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 rounded-2xl surface-premium p-5">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/12 to-primary/4 border border-primary/15 shrink-0">
                  <Clock className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Opening hours</p>
                  <dl className="mt-2 space-y-1">
                    {biz.hours.map((h) => (
                      <div key={h.day} className="flex items-center gap-3 text-sm">
                        <dt className="text-muted-foreground w-20">{h.day}</dt>
                        <dd className="font-semibold text-foreground tabular-nums">{h.time}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <a href={biz.instagramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-2xl surface-premium surface-premium-hover p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/12 to-primary/4 border border-primary/15">
                    <Instagram className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Instagram</p>
                    <p className="font-bold text-foreground text-sm truncate">{biz.instagram}</p>
                  </div>
                </a>
                <a href={biz.whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-2xl border border-[#25D366]/25 bg-[#25D366]/5 hover:bg-[#25D366]/10 hover:border-[#25D366]/40 transition-all p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#25D366]/15 border border-[#25D366]/25">
                    <MessageCircle className="h-4 w-4 text-[#25D366]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">WhatsApp</p>
                    <p className="font-bold text-[#1ea952] text-sm">Message us</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Quote Form */}
            <QuoteForm />
          </div>
        </div>
      </section>

      {/* Google Maps Embed */}
      <section className="border-t border-border bg-secondary/50 section-y">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="text-center max-w-xl mx-auto">
            <p className="eyebrow">Find the workshop</p>
            <h2 className="mt-3 display-lg text-foreground">14 Copenhagen Place, E14 7DF.</h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Sat-nav to Copenhagen Place — that's where the yard entrance is. Pixley Street is the registered address.
            </p>
          </div>

          <div className="mt-8 rounded-xl border border-border overflow-hidden aspect-[16/7] shadow-sm">
            <iframe
              src={biz.googleMapsEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Woodseer Autos location on Google Maps"
              className="h-full w-full"
            />
          </div>
          <div className="mt-4 text-center">
            <a
              href={biz.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-primary font-medium hover:text-primary/80 transition-colors"
            >
              <MapPin className="h-4 w-4" />
              Open in Google Maps →
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

function ChannelLink({
  href, icon: Icon, label, sub, external,
}: {
  href: string; icon: React.ComponentType<{ className?: string }>; label: string; sub: string; external?: boolean;
}) {
  if (!href) return null;
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="flex items-center gap-3 rounded-lg border border-border bg-background hover:bg-card transition-colors px-3 py-2.5"
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary border border-primary/20 shrink-0">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-bold text-foreground truncate">{label}</span>
        <span className="block text-[11px] text-muted-foreground">{sub}</span>
      </span>
    </a>
  );
}

